import type { BrowserLocalCacheStoreName } from './types'

export const BROWSER_LOCAL_CACHE_CROSS_TAB_CHANNEL = 'yijianmai-browser-local-cache-events-v1'
export const BROWSER_LOCAL_CACHE_CROSS_TAB_STORAGE_KEY = 'browser-local-cache:cross-tab-event:v1'

export type BrowserLocalCacheCrossTabEventType = 'cache:invalidate' | 'cache:write'

export type BrowserLocalCacheCrossTabEvent = {
  type: BrowserLocalCacheCrossTabEventType
  id: string
  senderId: string
  createdAt: number
  storeName?: BrowserLocalCacheStoreName
  cacheKey?: string
  userScope?: string
  resourceName?: string
  reason?: string
  deletedCount?: number
}

export type BrowserLocalCacheCrossTabEventInput = Omit<
  BrowserLocalCacheCrossTabEvent,
  'id' | 'senderId' | 'createdAt'
> & {
  id?: string
  senderId?: string
  createdAt?: number
}

const createRuntimeId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // Fall through to a deterministic-enough runtime id for old browsers/tests.
  }
  return `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

const browserLocalCacheSenderId = createRuntimeId()

const normalizeCrossTabEvent = (
  event: BrowserLocalCacheCrossTabEventInput
): BrowserLocalCacheCrossTabEvent => ({
  ...event,
  id: event.id || createRuntimeId(),
  senderId: event.senderId || browserLocalCacheSenderId,
  createdAt: event.createdAt || Date.now(),
})

const parseCrossTabEvent = (value: unknown): BrowserLocalCacheCrossTabEvent | null => {
  const event = value && typeof value === 'object'
    ? value as Partial<BrowserLocalCacheCrossTabEvent>
    : null
  if (!event || (event.type !== 'cache:invalidate' && event.type !== 'cache:write')) {
    return null
  }
  if (!event.id || !event.senderId || !Number.isFinite(Number(event.createdAt))) {
    return null
  }
  return event as BrowserLocalCacheCrossTabEvent
}

export const getBrowserLocalCacheCrossTabSenderId = (): string => browserLocalCacheSenderId

export const publishBrowserLocalCacheCrossTabEvent = (
  eventInput: BrowserLocalCacheCrossTabEventInput
): BrowserLocalCacheCrossTabEvent => {
  const event = normalizeCrossTabEvent(eventInput)
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      const channel = new BroadcastChannel(BROWSER_LOCAL_CACHE_CROSS_TAB_CHANNEL)
      channel.postMessage(event)
      channel.close()
    } catch {
      // localStorage fallback below still covers older or restricted browsers.
    }
  }
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      window.localStorage.setItem(BROWSER_LOCAL_CACHE_CROSS_TAB_STORAGE_KEY, JSON.stringify(event))
      window.localStorage.removeItem(BROWSER_LOCAL_CACHE_CROSS_TAB_STORAGE_KEY)
    } catch {
      // Cross-tab sync is best-effort; local cache operations remain authoritative.
    }
  }
  return event
}

export const publishBrowserLocalCacheInvalidation = (
  eventInput: Omit<BrowserLocalCacheCrossTabEventInput, 'type'>
): BrowserLocalCacheCrossTabEvent => publishBrowserLocalCacheCrossTabEvent({
  ...eventInput,
  type: 'cache:invalidate',
})

export const subscribeBrowserLocalCacheEvents = (
  handler: (event: BrowserLocalCacheCrossTabEvent) => void
): (() => void) => {
  const seenEventIds = new Set<string>()
  const handleEvent = (value: unknown) => {
    const event = parseCrossTabEvent(value)
    if (!event) return
    if (event.senderId === browserLocalCacheSenderId) return
    if (seenEventIds.has(event.id)) return
    seenEventIds.add(event.id)
    handler(event)
  }

  let channel: BroadcastChannel | null = null
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      channel = new BroadcastChannel(BROWSER_LOCAL_CACHE_CROSS_TAB_CHANNEL)
      channel.onmessage = (message) => handleEvent(message.data)
    } catch {
      channel = null
    }
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== BROWSER_LOCAL_CACHE_CROSS_TAB_STORAGE_KEY || !event.newValue) {
      return
    }
    try {
      handleEvent(JSON.parse(event.newValue))
    } catch {
      // Ignore malformed third-party storage writes.
    }
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', handleStorage)
  }

  return () => {
    if (channel) {
      channel.close()
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', handleStorage)
    }
  }
}
